import { useMemo, createContext } from 'react'
import { getProjectHumanCostApi, getProjectCostsApi } from '@/services'
import { useRequest } from "ahooks";
import { nanoid } from "nanoid"
import { add } from '@/utils'

type HumanBudget = Omit<API.PROJECT.ProjectBudget, 'standards' | 'budgetCosts'>;
export type HumanBudgetTableCol = HumanBudget & API.PROJECT.HumanBudgetItem & { row: number, parentID: string, itemID: string, usedPercent: number }

const getHumanBudgets = async (id: string | number, departs: string[]) => {
    const res = await getProjectHumanCostApi(id, departs)
    // const list: HumanBudgetTableCol[] = []
    // res.forEach(v => {
    //     if (!v.standards) return
    //     v.standards.forEach((sub, sub_i) => {
    //         sub.usedCost = 10000
    //     })
    // })
    return res || []
}

type ProjectBudget = Omit<API.PROJECT.ProjectBudget, 'standards' | 'budgetCosts'>;
export type ProjectBudgetTableCol = ProjectBudget & API.PROJECT.ProjectBudgetCostItem & { row: number, parentID: string, itemID: string, usedPercent: number }

const getProjectBudgets = async (id: string | number, departs: string[]) => {
    const res = await getProjectCostsApi(id, departs)
    // const list: ProjectBudgetTableCol[] = []
    // res.forEach(v => {
    //     if (!v.budgetCosts) return
    //     const parentID = nanoid()
    //     v.budgetCosts.forEach((sub, sub_i) => {
    //         const itemID = nanoid()
    //         const item = {
    //             ...v,
    //             ...sub,
    //             row: sub_i === 0 ? v.budgetCosts!.length : 0,
    //             parentID,
    //             itemID: itemID,
    //             usedPercent: sub.BudgetCost === 0 || sub.UsedCost === 0 ? 0 : Math.floor(sub.UsedCost * 100 / sub.BudgetCost || 0)
    //         }
    //         list.push(item)
    //     })
    // })
    return res || []
}

export const useCosts = (projectID: number | string, manual = true) => {
    const { data: humanCosts = [], loading: humancostLoading, run: humancostRun } = useRequest(getHumanBudgets, {
        defaultParams: [projectID, []],
        manual: manual
    });

    const { data: projectCosts = [], loading: projectcostLoading, run: projectcostRun } = useRequest(getProjectBudgets, {
        defaultParams: [projectID, []],
        manual: manual
    });

    const tablData = useMemo(() => {
        const map = new Map<string, API.PROJECT.ProjectBudget>()
        humanCosts.forEach(v => {
            if (!v.standards) {
                v.standards = []
            }
            const key = v.companyCode + v.departmentCode + v.majorCode
            const item = map.get(key)
            let newItem = {
                ...v
            }
            if (item) {
                let standards = [...(item.standards || []), ...v.standards]
                newItem = {
                    ...item,
                    ...newItem,
                    standards
                }
            }
            map.set(key, newItem)
        })
        projectCosts.forEach(v => {
            if (!v.budgetCosts) {
                v.budgetCosts = []
            }
            const key = v.companyCode + v.departmentCode + v.majorCode
            const item = map.get(key)
            let newItem = {
                ...v
            }
            if (item) {
                let budgetCosts = [...(item.budgetCosts || []), ...v.budgetCosts]
                newItem = {
                    ...newItem,
                    ...item,
                    budgetCosts
                }
            }
            map.set(key, newItem)
        })
        return [...map.values()].map(v => {
            let humanDepartTotal = v.totalBudgetCost || 0
            let humanDepartUsedTotal = 0
            if (v.standards && v.standards.length > 0) {
                v.standards.forEach(item => {
                    // humanDepartTotal = add(item.budgetCost, humanDepartTotal, 2)
                    humanDepartUsedTotal = add(item.usedCost, humanDepartUsedTotal, 2)
                })
            }
            let projectDepartTotal = 0
            let projectDepartUsedTotal = 0
            if (v.budgetCosts) {
                v.budgetCosts.forEach(item => {
                    projectDepartTotal = add(item.BudgetCost, projectDepartTotal, 2)
                    projectDepartUsedTotal = add(item.UsedCost, projectDepartUsedTotal, 2)
                })
            }
            const allDepartTotal = add(humanDepartTotal, projectDepartTotal, 2)
            const allDepartUsedTotal = add(humanDepartUsedTotal, projectDepartUsedTotal, 2)
            let maxrow = Math.max(v.budgetCosts?.length || 0, v.standards?.length || 0, 1)
            const parentID = nanoid()
            return {
                ...v,
                pid: parentID,
                maxrows: new Array(maxrow).fill(0).map((_, index) => {
                    let humanCost = v.standards ? (index < v.standards.length ? v.standards[index] : null) : null
                    if (humanCost) {
                        humanCost.usedPercent = getPercent(humanCost.usedCost, humanCost.budgetCost)
                    }
                    let projectCost = v.budgetCosts ? (index < v.budgetCosts.length ? v.budgetCosts[index] : null) : null
                    if (projectCost) {
                        projectCost.usedPercent = getPercent(projectCost.UsedCost, projectCost.BudgetCost)
                    }
                    return {
                        sid: nanoid(),
                        humanCost,
                        projectCost
                    }
                }),
                humanDepartTotal,
                projectDepartTotal,
                allDepartTotal,
                humanDepartUsedTotal,
                projectDepartUsedTotal,
                allDepartUsedTotal,
                humanDepartPercent: getPercent(humanDepartUsedTotal, humanDepartTotal),
                projectDepartPercent: getPercent(projectDepartUsedTotal, projectDepartTotal),
                allDepartPercent: getPercent(allDepartUsedTotal, allDepartTotal),
            }
        })
    }, [humanCosts, projectCosts])

    const totals = useMemo(() => {
        let humanTotal = 0, humanUsedTotal = 0
        let projectTotal = 0, projectUsedTotal = 0

        humanCosts.forEach(v => {
            humanTotal = add(humanTotal, v.totalBudgetCost || 0, 2)
            // if (!v.standards || v.standards.length === 0) {
            //     humanTotal = add(humanTotal, v.totalBudgetCost || 0, 2)
            //     return
            // }
            if (v.standards) {
                v.standards.forEach(item => {
                    humanUsedTotal = add(humanUsedTotal, item.usedCost, 2)
                })
            }
            
        })


        projectCosts.forEach(v => {
            if (!v.budgetCosts) return
            v.budgetCosts.forEach(item => {
                projectTotal = add(projectTotal, item.BudgetCost, 2)
                projectUsedTotal = add(projectUsedTotal, item.UsedCost, 2) 
            })
        })
        const allTotal = add(humanTotal, projectTotal, 2)
        const allUsedTotal = add(humanUsedTotal, projectUsedTotal, 2)
        return {
            humanTotal,
            humanUsedTotal,
            humanUsedTotalPercent: getPercent(humanUsedTotal, humanTotal),
            projectTotal,
            projectUsedTotal,
            projectUsedTotalPercent: getPercent(projectUsedTotal, projectTotal),
            allTotal,
            allUsedTotal,
            allUsedTotalPercent: getPercent(allUsedTotal, allTotal)
       }
    }, [humanCosts, projectCosts])

    const run = (departs: string[]) => {
        humancostRun(projectID, departs)
        projectcostRun(projectID, departs)
    }
   
    return {
        tablData,
        loading: humancostLoading || projectcostLoading,
        totals,
        run,
        humancostRun,
        projectcostRun,
    }
}

const getPercent = (used: number, amount: number) => {
    if(amount === 0 && used > 0) return NaN
    if (used === 0 || amount === 0) return 0
    return Number((used * 100 / amount).toFixed(1))
}
interface ICostContext {
    departs: Department[],
    departsLoading: boolean
}
export const CostContext = createContext<ICostContext | null>(null)